//src/app/features/profile/viewProfile/index.tsx
import {
  DashboardContainerHeader,
  DashboardSubTabs,
  ScrollContainer,
} from 'app/layout/dashboardLayout/BoxLayout';
import { AddIcon, QBOIcon } from 'assets/icons'; // Import QBO Icon here
import { CustomButton } from 'Components/Auth/CustomButton';
import { Outlet, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { prepareHeaders } from 'utils'; // For preparing auth headers
import { useGetMyProfileQuery } from 'app/services/user'; // Import the query to get user data
import { successToast, errorToast } from 'Components/Notifications'; // Import notification functions
import useConfirmContinue from 'app/hooks/useConfirmContinue'; // Import the confirm hook

export interface IViewProfileProps {}

export default function ViewProfile(props: IViewProfileProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmContinue = useConfirmContinue();

  // Fetch user profile data
  const { data: userData } = useGetMyProfileQuery();
  const user = userData?.user;

  // Determine QuickBooks connection status
  const getQuickBooksStatus = () => {
    if (!user?.quickbooks?.quickbooks_token_expires_at) return 'Connect QBO';

    const expiresAt = new Date(
      user.quickbooks.quickbooks_token_expires_at,
    ).getTime();
    const now = new Date().getTime();

    if (expiresAt < now) {
      return 'Reconnect QBO';
    } else {
      return 'Connected QBO';
    }
  };

  // Handle QuickBooks disconnection
  const handleDisconnectQuickBooks = async () => {
    try {
      setIsLoading(true);
      const headers = await prepareHeaders(new Headers());
      headers.append('Content-Type', 'application/json');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/quick_books/disconnect`,
        {
          method: 'DELETE',
          headers,
        },
      );

      if (response.ok) {
        successToast('QuickBooks disconnected successfully!');
        window.location.reload();
      } else {
        const errorData = await response.json();
        errorToast(errorData.error || 'Failed to disconnect QuickBooks');
      }
    } catch (error) {
      console.error('Error disconnecting QuickBooks:', error);
      errorToast('Error disconnecting QuickBooks');
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm and handle disconnection
  const confirmDisconnectQuickBooks = () => {
    confirmContinue({
      title: 'Disconnect QuickBooks',
      text: (
        <div className="max-w-xs">
          Are you sure you want to disconnect from QBO?{' '}
        </div>
      ),
      onConfirm: handleDisconnectQuickBooks,
    });
  };

  // Button label based on QuickBooks connection status
  const quickBooksButtonText = getQuickBooksStatus();

  // Handle QuickBooks connect/reconnect/disconnect logic
  const handleQuickBooksButtonClick = () => {
    const quickBooksStatus = getQuickBooksStatus();

    if (quickBooksStatus === 'Connected QBO') {
      confirmDisconnectQuickBooks();
    } else {
      handleConnectQuickBooks();
    }
  };

  // Function to handle QuickBooks connection or reconnection
  const handleConnectQuickBooks = async () => {
    const quickBooksStatus = getQuickBooksStatus();

    if (quickBooksStatus === 'Reconnect QBO') {
      try {
        setIsLoading(true);
        const headers = await prepareHeaders(new Headers());
        headers.append('Content-Type', 'application/json');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/quick_books/refresh`,
          {
            method: 'PATCH',
            headers,
          },
        );

        if (response.status === 204) {
          setError(null);
          successToast('QuickBooks token refreshed successfully!');
          window.location.reload();
          return; // Exit if refresh is successful
        } else {
          const errorData = await response.json();
          errorToast(errorData.error || 'Failed to refresh QuickBooks token');
        }
      } catch (error) {
        console.error('Error refreshing QuickBooks token:', error);
        errorToast('QuickBooks refresh failed. Reconnecting...');
      } finally {
        setIsLoading(false);
      }

      // If refresh fails, proceed with Connect QBO
    }

    // Attempt to connect if refresh was unsuccessful or if initial status is Connect QBO
    try {
      setIsLoading(true);
      const headers = await prepareHeaders(new Headers());
      headers.append('Content-Type', 'application/json');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/quick_books/connect`,
        {
          method: 'GET',
          headers,
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (data.redirect_url) {
          window.open(data.redirect_url, '_blank');
        } else {
          errorToast('No redirect URL found');
        }
      } else {
        const errorData = await response.json();
        errorToast(errorData.error || 'Failed to connect QBO');
      }
    } catch (error) {
      console.error('Error connecting QBO:', error);
      errorToast('Error connecting QBO');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DashboardContainerHeader title="My Account">
        <div className="flex flex-wrap items-center">
          <DashboardSubTabs
            showBorder={false}
            showActiveBorder={false}
            basePath="/dashboard/profile"
            tabs={[
              { to: 'account', text: 'Account Info' },
              { to: 'personalDetails', text: 'Personal Details' },
              { to: 'businesses', text: 'Business Details' },
            ]}
          />

          <CustomButton
            text="Add Business"
            borderColor="green"
            bgColor="white"
            textColor="green"
            width="min-w-max whitespace-nowrap"
            height="10"
            textSize="text-sm"
            prefix={<AddIcon />}
            onClick={() => navigate('/dashboard/profile/businesses/create')}
          />

          <CustomButton
            text={quickBooksButtonText}
            borderColor="green"
            bgColor="white"
            textColor={
              quickBooksButtonText === 'Connected QBO' ? 'gray-700' : 'green'
            }
            width="min-w-max whitespace-nowrap ml-3"
            height="10"
            textSize="text-sm"
            prefix={<QBOIcon className="w-5 h-5" />}
            onClick={handleQuickBooksButtonClick}
            disabled={isLoading} // Prevent action during loading
          />

          {error && <p className="text-red-500 ml-4">{error}</p>}
        </div>
      </DashboardContainerHeader>

      <ScrollContainer className="">
        <Outlet />
      </ScrollContainer>
    </>
  );
}
